import type { Level } from "building-types/levels/levelTypes";
import type { GeneratedRoof } from "building-types/roofs/generatedRoofSystemTypes";
import type { MultiGableRoofClipContext } from "building-types/roofs/multiGableRoofClipContextTypes";
import { RoofSide } from "building-types/settings/roofTypes";
import { GableRoofType } from "building-types/settings/settingsTypesEnum";
import type { WallSideDescriptor, WallDescriptor } from "building-types/walls/wallDesciptorTypes";
import { WallType } from "building-types/walls/wallEnums";
import type { WallFaces } from "building-types/walls/wallFacesTypes";
import type { WallSystem } from "building-types/walls/wallSytemTypes";
import type { WallTable } from "building-types/walls/wallTableTypes";
import type { SolidWall } from "building-types/walls/wallTypes";
import { GABLE_ROOF_THICKNESS } from "common/constants/buildingObjects";
import { levelInchesNew } from "common/utils/nominalNumbers";
import { MULTIGABLE_BSP_EPSILON } from "doc/utils/roofTools";
import type { Face } from "geom/brep";
import { collectUniqueTags, faceAABB3, facesAABB3, tagFaces } from "geom/brep";
import { addConvexPlanesToBSPLeaf } from "geom/bsp";
import type { ClipFaceContext } from "geom/bspClipping";
import { splitFacesBSP, clippingNodeNew, clipFace, clipFaces } from "geom/bspClipping";
import type { NodeBSP } from "geom/bspTypes";
import { extrudeFaceAlongNormal, extrusionToBRep } from "geom/extrude";
import { planeDistanceTo, planeNewXY } from "geom/plane";
import { planeBSPFromPlane, planeBSPNew } from "geom/planeBSP";
import type { PolygonPlanarGraph } from "geom/polygonPlanarGraph";
import { getWallPath } from "../walls/wall";
import { clipWallFacesWithMultipleContexts, operateWallFaces } from "../walls/wallCut";
import type { RoofWallSystemPerimeter } from "./roofWallSystemTypes";

interface ClipRoofWallSystemOptions {
  readonly wallSystem: WallSystem;
  readonly roofLevel: Level;
  readonly roof: GeneratedRoof;
  readonly roofWallPerimeters: RoofWallSystemPerimeter[];
  readonly planarGraph: PolygonPlanarGraph;
  readonly upperFloorsRoomsBSPs: NodeBSP[];
  readonly wallOverlapBSPs: NodeBSP[];
  readonly halfStoriesBSP: NodeBSP | null;
  readonly roofWallDiscardBSP: NodeBSP;
  readonly extraWallCutBSPs: NodeBSP[];
  readonly clipContext: MultiGableRoofClipContext;
  readonly fillGaps: boolean;
}

export function clipRoofWallSystem({
  wallSystem,
  roofLevel,
  roof,
  roofWallPerimeters,
  planarGraph,
  upperFloorsRoomsBSPs,
  wallOverlapBSPs,
  halfStoriesBSP,
  roofWallDiscardBSP,
  extraWallCutBSPs,
  clipContext,
  fillGaps,
}: ClipRoofWallSystemOptions): WallSystem {
  const { discardBSP, roofsBSP, insideBSP } = clipContext;
  const { roofVolume, roofType, legacyRoof } = roof;
  const { verticalRails, baseHeight, heelHeightOffsets } = roofVolume;
  const { isStartTruncated, isEndTruncated } = verticalRails;
  const { floorElevation } = roofLevel;
  const { walls } = wallSystem;

  const removeBottomBackBSP = addConvexPlanesToBSPLeaf([
    planeBSPNew(0, 0, -1, -baseHeight + heelHeightOffsets.refOffset),
  ]);

  const clipBackWallsOptions: ClipFaceContext = {
    clippingNodes: [clippingNodeNew(roofVolume.discardWallsBSP, true), clippingNodeNew(removeBottomBackBSP, true)],
    epsilon: MULTIGABLE_BSP_EPSILON,
  };

  const rebuildBackFaces = (wall: SolidWall): SolidWall => {
    const { wallFaces, mass } = wall;
    const path = getWallPath(wall);
    const { startX, startY } = path;
    const { plane: leftPlane } = mass.leftFace;
    const { rightFaces } = wallFaces;
    let newLeftFaces: Face[] = [];

    for (const rightFace of rightFaces) {
      const { plane: rightPlane } = rightFace;
      const rightDist = planeDistanceTo(rightPlane, startX, startY, 0);
      const leftDist = planeDistanceTo(leftPlane, startX, startY, 0);
      const thickness = leftDist + rightDist;

      const extrusion = extrudeFaceAlongNormal(rightFace, -thickness);
      const [, backFace] = extrusionToBRep(extrusion).faces;

      newLeftFaces.push(backFace);
    }

    newLeftFaces = clipFaces({
      faces: newLeftFaces,
      options: clipBackWallsOptions,
    });

    const tag = { type: "OPERATION", operation: "REBUILT_ROOF_WALL_FACE" };

    return {
      ...wall,
      wallFaces: {
        ...wallFaces,
        leftFaces: tagFaces(newLeftFaces, tag),
      },
    };
  };

  const adjustRoofTopDatum = (wall: SolidWall, roofSide: RoofSide): SolidWall => {
    const { wallFaces } = wall;
    const path = getWallPath(wall);
    const { leftFaces, rightFaces } = wallFaces;
    const { wallDescriptor } = path;
    const { leftSide, rightSide } = wallDescriptor;
    const { wallSideDatums: leftDatums } = leftSide;
    const { wallSideDatums: rightDatums } = rightSide;
    const isTruncated =
      (roofSide === RoofSide.START && isStartTruncated) || (roofSide === RoofSide.END && isEndTruncated);
    if (!isTruncated) {
      return wall;
    }

    // sets the roof top datum to the highest point of the wall faces after clipping
    const leftAABB3 = facesAABB3(leftFaces);
    const rightAABB3 = facesAABB3(rightFaces);

    let roofTop = Math.max(leftDatums.roofTop, rightDatums.roofTop);

    if (leftAABB3) {
      roofTop = Math.min(roofTop, leftAABB3.maxZ + GABLE_ROOF_THICKNESS + 1 - floorElevation);
    }

    if (rightAABB3) {
      roofTop = Math.min(roofTop, rightAABB3.maxZ + GABLE_ROOF_THICKNESS + 1 - floorElevation);
    }

    const newLeftSide: WallSideDescriptor = {
      ...leftSide,
      wallSideDatums: {
        ...leftDatums,
        roofTop: levelInchesNew(roofTop),
      },
    };

    const newRightSide: WallSideDescriptor = {
      ...rightSide,
      wallSideDatums: {
        ...rightDatums,
        roofTop: levelInchesNew(roofTop),
      },
    };

    const newWallDescriptor: WallDescriptor = {
      ...wallDescriptor,
      leftSide: newLeftSide,
      rightSide: newRightSide,
    };

    return {
      ...wall,
      mass: {
        ...wall.mass,
        path: {
          ...path,
          wallDescriptor: newWallDescriptor,
        },
      },
    };
  };

  // removes extra wall cuts
  const removeExtraWallCuts: ClipFaceContext = {
    clippingNodes: [...extraWallCutBSPs.map((x) => clippingNodeNew(x))],
    epsilon: MULTIGABLE_BSP_EPSILON,
  };

  // removes wall sections that are above the roof
  const removeSideWallsAboveRoof: ClipFaceContext = {
    clippingNodes: [clippingNodeNew(roofVolume.discardWallsBSP, true), clippingNodeNew(discardBSP, true)],
    epsilon: MULTIGABLE_BSP_EPSILON,
  };

  const removeStartWallAboveRoof: ClipFaceContext = {
    clippingNodes: [clippingNodeNew(roofVolume.discardStartWallBSP, true), clippingNodeNew(discardBSP, true)],
    epsilon: MULTIGABLE_BSP_EPSILON,
  };

  const removeEndWallAboveRoof: ClipFaceContext = {
    clippingNodes: [clippingNodeNew(roofVolume.discardEndWallBSP, true), clippingNodeNew(discardBSP, true)],
    epsilon: MULTIGABLE_BSP_EPSILON,
  };

  // removes wall sections that are inside the house
  const removeSideWallsInside: ClipFaceContext = {
    clippingNodes: [
      clippingNodeNew(fillGaps || legacyRoof ? insideBSP : roofsBSP),
      ...upperFloorsRoomsBSPs.map((x) => clippingNodeNew(x)),
    ],
    coplanarsToFront: true,
    epsilon: MULTIGABLE_BSP_EPSILON,
  };

  const removeGableWallsInside: ClipFaceContext = {
    clippingNodes: [clippingNodeNew(insideBSP), ...upperFloorsRoomsBSPs.map((x) => clippingNodeNew(x))],
    coplanarsToFront: true,
    epsilon: MULTIGABLE_BSP_EPSILON,
  };

  // removes wall sections overlapping with other walls
  const removeOverlappingWalls: ClipFaceContext = {
    clippingNodes: [...wallOverlapBSPs.map((x) => clippingNodeNew(x)), clippingNodeNew(roofWallDiscardBSP)],
    coplanarsToFront: legacyRoof && roofType === GableRoofType.DEFAULT,
    checkCoplanarFacing: true,
    epsilon: MULTIGABLE_BSP_EPSILON,
  };

  // special clipping for shed roof walls
  const removeInvalidShedWalls: ClipFaceContext = {
    clippingNodes: [clippingNodeNew(discardBSP, true)],
    epsilon: MULTIGABLE_BSP_EPSILON,
  };

  // create the list of clipping options
  const forcedStartGableWallClipOptionList: ClipFaceContext[] = [
    removeExtraWallCuts,
    removeStartWallAboveRoof,
    removeOverlappingWalls,
  ];

  const forcedEndGableWallClipOptionList: ClipFaceContext[] = [
    removeExtraWallCuts,
    removeEndWallAboveRoof,
    removeOverlappingWalls,
  ];

  const forcedSideWallClipOptionList: ClipFaceContext[] = [
    removeExtraWallCuts,
    removeSideWallsAboveRoof,
    removeOverlappingWalls,
  ];

  const startGableWallClipOptionList: ClipFaceContext[] = [
    removeExtraWallCuts,
    removeStartWallAboveRoof,
    removeGableWallsInside,
    removeOverlappingWalls,
  ];

  const endGableWallClipOptionList: ClipFaceContext[] = [
    removeExtraWallCuts,
    removeEndWallAboveRoof,
    removeGableWallsInside,
    removeOverlappingWalls,
  ];

  const sideWallClipOptionList: ClipFaceContext[] = [
    removeExtraWallCuts,
    removeSideWallsAboveRoof,
    removeSideWallsInside,
    removeOverlappingWalls,
  ];

  if (roofType !== GableRoofType.DEFAULT) {
    startGableWallClipOptionList.push(removeInvalidShedWalls);
    endGableWallClipOptionList.push(removeInvalidShedWalls);
    sideWallClipOptionList.push(removeInvalidShedWalls);
  }

  const newWalls: WallTable = {};
  for (const wallId in walls) {
    const wall = walls[wallId];

    // ignore void walls
    if (wall.wallType !== WallType.SOLID) {
      newWalls[wallId] = wall;
      continue;
    }

    const { wallFaces } = wall;
    const path = getWallPath(wall);
    const { halfEdgeIndex } = path;
    const edgeRef = planarGraph.halfEdgeIndexToPolygonEdgeRef(halfEdgeIndex);

    if (!edgeRef) {
      continue;
    }

    const { index, edgeIndex } = edgeRef;

    if (index >= roofWallPerimeters.length) {
      continue;
    }

    const { roofWallEdges } = roofWallPerimeters[index];

    if (edgeIndex >= roofWallEdges.length) {
      continue;
    }

    const roofWallEdge = roofWallEdges[edgeIndex];
    const { roofSide, forceAddWall } = roofWallEdge;

    if (fillGaps) {
      clipHalfStoryWallFaces(wallFaces, halfStoriesBSP, roof);
    }

    if (roofSide === RoofSide.START) {
      clipWallFacesWithMultipleContexts(
        wallFaces,
        forceAddWall ? forcedStartGableWallClipOptionList : startGableWallClipOptionList,
      );
    } else if (roofSide === RoofSide.END) {
      clipWallFacesWithMultipleContexts(
        wallFaces,
        forceAddWall ? forcedEndGableWallClipOptionList : endGableWallClipOptionList,
      );
    } else {
      clipWallFacesWithMultipleContexts(
        wallFaces,
        forceAddWall ? forcedSideWallClipOptionList : sideWallClipOptionList,
      );
    }

    newWalls[wallId] = adjustRoofTopDatum(wall, roofSide);

    if (!forceAddWall) {
      newWalls[wallId] = rebuildBackFaces(newWalls[wallId]);
    }
  }

  return {
    ...wallSystem,
    walls: newWalls,
  };
}

function clipHalfStoryWallFaces(
  wallFaces: WallFaces,
  halfStoriesBSP: NodeBSP | null,
  subRoof: GeneratedRoof,
  onlyLeftFaces: boolean = false,
) {
  if (!halfStoriesBSP) {
    return;
  }

  const { roofVolume } = subRoof;
  const { baseHeight, heelHeightOffsets } = roofVolume;
  const refHeight = baseHeight - Math.max(heelHeightOffsets.refOffset, 0);
  const plane = planeNewXY(0, 0, refHeight);
  const planeBSP = planeBSPFromPlane(plane);
  const splitBSP = addConvexPlanesToBSPLeaf([planeBSP]);

  // split faces by the base height of the roof
  const splitPred = (faces: Face[]): Face[] => {
    const outFaces: Face[] = [];

    const splitOptions = {
      node: splitBSP,
      frontFaces: outFaces,
      backFaces: outFaces,
      epsilon: MULTIGABLE_BSP_EPSILON,
    };

    splitFacesBSP(faces, splitOptions);

    const tags = collectUniqueTags(faces);
    if (tags.length > 0) {
      tagFaces(outFaces, tags);
    }

    return outFaces;
  };

  operateWallFaces(wallFaces, splitPred, onlyLeftFaces);

  // clip only the faces below the base height of the roof
  const halfStoryOptions: ClipFaceContext = {
    clippingNodes: [clippingNodeNew(halfStoriesBSP, true)],
    epsilon: MULTIGABLE_BSP_EPSILON,
  };

  const clipPred = (faces: Face[]): Face[] => {
    const outFaces: Face[] = [];

    for (const face of faces) {
      const { maxZ } = faceAABB3(face);
      if (maxZ < refHeight + MULTIGABLE_BSP_EPSILON) {
        clipFace({
          face,
          options: halfStoryOptions,
          clippedFaces: outFaces,
        });
      } else {
        outFaces.push(face);
      }
    }

    return outFaces;
  };

  operateWallFaces(wallFaces, clipPred, onlyLeftFaces);
}
